/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import {
	MonthRange,
	ScheduleViewMode,
	WeekRange,
} from '../../types/schedule.types';
import {
	getCalendarDays,
	getMonthRange,
	getWeekRange,
	isDateInRange,
	isSameDay,
	toDateKey,
} from '../../utils/dateUtils';
import styles from '../../styles/scheduler/CalendarWeekGrid.module.css';

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * Generalisation of CalendarWeekGrid.
 *
 * selectionMode='week'  -> hovering/clicking a row selects that Mon–Sun week
 *                          (unchanged behaviour).
 * selectionMode='month' -> the whole month range (4–6 full weeks) is
 *                          highlighted; clicking any day selects the month
 *                          that day belongs to. For a 4-week February
 *                          starting on Monday, exactly 4 rows highlight —
 *                          there are no spill-over days to dim.
 */
interface CalendarGridProps {
	month: number;
	year: number;
	selectionMode: ScheduleViewMode;
	selectedWeek: WeekRange | null;
	selectedMonth: MonthRange | null;
	eventDots: Set<string>;
	onSelectWeek: (week: WeekRange) => void;
	onSelectMonthRange: (month: MonthRange) => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
	month,
	year,
	selectionMode,
	selectedWeek,
	selectedMonth,
	eventDots,
	onSelectWeek,
	onSelectMonthRange,
}) => {
	const [hoveredWeek, setHoveredWeek] = useState<WeekRange | null>(null);
	const today = new Date();
	const days = getCalendarDays(year, month);

	const weeks: Date[][] = [];
	for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

	const isWeekMode = selectionMode === 'week';
	const visibleMonthRange = getMonthRange(year, month);

	// Week mode: highlight the hovered/selected week.
	// Month mode: ALWAYS highlight the full span of the month currently
	//   shown in the popup, so the user sees exactly what clicking will
	//   select (the whole month, not a single week). Previously this only
	//   highlighted when the existing selection already matched the visible
	//   month, so before the first month-click the grid showed only a week
	//   highlighted — which read as "selecting a week in month view".
	const activeRange: WeekRange | null = isWeekMode
		? hoveredWeek || selectedWeek
		: visibleMonthRange;

	const handleRowClick = (weekRange: WeekRange, firstDay: Date) => {
		if (isWeekMode) {
			onSelectWeek(weekRange);
		} else {
			// Select the month being viewed, regardless of which row is hit.
			onSelectMonthRange(visibleMonthRange);
		}
	};

	return (
		<div className={styles.grid}>
			<div className={styles.dayNames}>
				{DAYS.map(d => (
					<div key={d} className={styles.dayName}>
						{d}
					</div>
				))}
			</div>
			{weeks.map((weekDays, wi) => {
				const weekRange = getWeekRange(weekDays[0]);
				const isSelected = isWeekMode
					? selectedWeek
						? isSameDay(selectedWeek.start, weekRange.start)
						: false
					: false; // month mode: highlight per-day, not per-row
				const isHovered =
					isWeekMode && hoveredWeek
						? isSameDay(hoveredWeek.start, weekRange.start)
						: false;

				return (
					<div
						key={wi}
						className={`${styles.weekRow} ${isSelected || isHovered ? styles.weekRowHighlighted : ''}`}
						onClick={() => handleRowClick(weekRange, weekDays[0])}
						onMouseEnter={() =>
							isWeekMode && setHoveredWeek(weekRange)
						}
						onMouseLeave={() => isWeekMode && setHoveredWeek(null)}
					>
						{weekDays.map((date, di) => {
							const isCurrentMonth = date.getMonth() === month;
							const isToday = isSameDay(date, today);
							const hasEvent = eventDots.has(toDateKey(date));
							const inActive = activeRange
								? isDateInRange(date, activeRange)
								: false;
							// Highlight only the days that belong to the
							// visible month in BOTH modes. Spill-over days
							// from adjacent months stay dimmed (.dayOther),
							// even though month mode still fetches them.
							const highlight = inActive && isCurrentMonth;

							return (
								<div
									key={di}
									className={[
										styles.day,
										!isCurrentMonth ? styles.dayOther : '',
										highlight ? styles.dayInRange : '',
									].join(' ')}
								>
									<span
										className={`${styles.dayNum} ${isToday ? styles.dayNumToday : ''}`}
									>
										{date.getDate()}
									</span>
									{hasEvent && (
										<span className={styles.dot} />
									)}
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
};

export default CalendarGrid;
